import React, { useState } from "react";
import { LazyLoadingApiResponse } from "../../../types/api";
import { StylesConfig } from "react-select";

interface Option {
  value: string;
  label: string;
}

interface LazyMultiSelectProps {
  initialValue?: string[];
  onSelectionChange: (values: string[]) => void;
  fetchFunction: (query: string, page: number, limit: number) => Promise<LazyLoadingApiResponse>;
  placeholder?: string;
  className?: string;
  itemsPerPage?: number;
}


const LazyMultiSelect: React.FC<LazyMultiSelectProps> = ({
  initialValue = [],
  onSelectionChange,
  fetchFunction,
  placeholder = "Select...",
  className = "",
  itemsPerPage = 10
}) => {
  // State for options and selected values
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedValues, setSelectedValues] = useState<Option[]>([]);
  const [inputValue, setInputValue] = useState("");
  
  // State for lazy loading
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0); // Changed from 1 to 0 to start at page 0
  const [hasMore, setHasMore] = useState(true);
  
  // Reference for custom debounce timer
  const debounceTimer = React.useRef<NodeJS.Timeout | null>(null);
  // To track if the component is mounted
  const isMounted = React.useRef(true);

  // Function to load options from the API
  const fetchOptions = async (searchQuery: string, pageNumber: number) => {
    if (!isMounted.current) return;
    setIsLoading(true);
    
    try {
      // Use the API function passed as prop with pageNumber starting at 0
      const data = await fetchFunction(searchQuery, pageNumber, itemsPerPage);
      
      if (!isMounted.current) return;
      
      // Map returned data to our option format
      // Adapt the mapping to your API format
      const newOptions = data.values.map((value) => ({
        value: value,
        label: value
      }));
      
      if (pageNumber === 0) {
        // First page: replace options but keep selected ones
        const selectedValuesArray = selectedValues.map(option => option.value);
        const selectedOptions = selectedValues.filter(
          option => !newOptions.some(newOption => newOption.value === option.value)
        );
        setOptions([...selectedOptions, ...newOptions]);
      } else {
        // Next pages: add to existing options
        setOptions(prev => [...prev, ...newOptions]);
      }
      
      // Update if more pages are available
      setHasMore(data.hasMore || false);
    } catch (error) {
      console.error("Error loading options:", error);
      if (isMounted.current) {
        // Don't clear selected options in case of error
        setOptions([...selectedValues]);
        setHasMore(false);
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };
  
  // Initialize with default values
  React.useEffect(() => {
    isMounted.current = true;
    
    // Delayed loading to give time for authentication to load
    const timer = setTimeout(() => {
      if (isMounted.current) {
        fetchOptions("", 0); // Changed from 1 to 0
      }
    }, 100);
    
    // Clean up timer when component is destroyed
    return () => {
      isMounted.current = false;
      clearTimeout(timer);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);
  
  // Effect to synchronize selected values with initialValue
  React.useEffect(() => {
    if (initialValue && initialValue.length > 0 && options.length > 0) {
      const matchingOptions = options.filter(option => 
        initialValue.includes(option.value)
      );
      
      // Check if options have changed to avoid infinite loops
      if (JSON.stringify(matchingOptions) !== JSON.stringify(selectedValues)) {
        setSelectedValues(matchingOptions);
      }
    }
  }, [initialValue, options]);
  
  // Custom debounce function for search
  const debouncedSearch = (query: string) => {
    // Cancel previous timer if it exists
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // Create a new timer
    debounceTimer.current = setTimeout(() => {
      // Don't clear existing options during search
      // setOptions([]); <-- Removed this line which was causing the problem
      setPage(0); // Changed from 1 to 0
      fetchOptions(query, 0); // Changed from 1 to 0
    }, 300);
  };
  
  const handleInputChange = (newValue: string, { action }: any) => {
    if (action === "input-change") {
      setInputValue(newValue);
      debouncedSearch(newValue);
    }
  };
  
  // Handler for scrolling to the bottom (lazy loading)
  const handleMenuScrollToBottom = () => {
    if (!isLoading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchOptions(inputValue, nextPage);
    }
  };
  
  // Handler for option selection
  const handleChange = (selected: readonly Option[]) => {
    const selectedOpts = selected as Option[];
    setSelectedValues(selectedOpts);
    
    // Notify parent of selected values
    const values = selectedOpts.map(option => option.value);
    onSelectionChange(values);
  };
  
  // Function to select all filtered options when Enter is pressed
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      
      // Get all currently filtered options that aren't already selected
      const filteredOptions = options.filter(option => 
        !selectedValues.some(selected => selected.value === option.value)
      );
      
      if (filteredOptions.length > 0) {
        // Combine with existing selections
        const newSelectedValues = [...selectedValues, ...filteredOptions];
        setSelectedValues(newSelectedValues);
        
        // Notify parent of selected values
        const values = newSelectedValues.map(option => option.value);
        onSelectionChange(values);
        
        // Clear input field
        setInputValue("");
      }
    }
  };
  
  // Import react-select dependencies
  const Select = require("react-select").default;
  const makeAnimated = require("react-select/animated").default;
  const animatedComponents = makeAnimated();
  const customStyles: StylesConfig<Option, true> = {
    control: (base) => ({
      ...base,
      minHeight: 60,
    }),
    menu: (base) => ({
      ...base,
      minWidth: 250,
    }),
    placeholder: (base) => ({
      ...base,
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'italic',
      color: '#D9D7D4',
    }),
  };
  
  return (
    <div className={className}>
      <Select
        options={options}
        isMulti
        closeMenuOnSelect={false}
        onChange={handleChange}
        value={selectedValues}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onMenuScrollToBottom={handleMenuScrollToBottom}
        isLoading={isLoading}
        placeholder={placeholder}
        loadingMessage={() => "Loading..."}
        styles={customStyles}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

export default LazyMultiSelect;